import clsx from "clsx";
import { Check, ChevronDown, Eye, UserCog, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Direx, DirexRole, DirexStation } from "../../api/types";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Button, inputClass, SectionLabel } from "../../components/ui";
import { k, t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { roleLabel, terminalName } from "./common";
import { usePopover } from "./usePopover";

/** Rôle de ce poste dans la DIREX du dossier : l'animateur dirige l'exercice, une cellule du centre opérations
 *  (public entraîné) ne voit que les incidents injectés et traite les siens, l'observateur suit tout sans rien
 *  modifier. Chaque poste choisit le sien ; les autres postes du dossier sont listés. */

export const ROLE_ICON = { animateur: UserCog, cellule: Users, observateur: Eye } as const;

const CHOICES: { role: DirexRole; label: string; hint: string }[] = [
  { role: "animateur", label: k("Animateur"), hint: k("Dirige l'exercice : génère la MEL/MIL, injecte, règle l'horloge.") },
  {
    role: "cellule",
    label: k("Cellule"),
    hint: k("Public entraîné : voit les incidents injectés et marque « traité » ceux de sa cellule."),
  },
  { role: "observateur", label: k("Observateur"), hint: k("Suit tout l'exercice sans rien modifier ; renseigne la 3A.") },
];

/** Participant en mode comptes (docs/17 §3.7, §8.3) : « CNE MARTIN · Cellule S2 · sur PC OPS 2, iPad 3 », deux postes
 *  nommés puis « +{n} » ; sans session ouverte, « hors ligne ». */
export function participantLine(s: DirexStation): string {
  const role = roleLabel(s.role, s.label);
  const seats = s.seats ?? [];
  if (seats.length === 0) return t("{nom} · {role} · hors ligne", { nom: s.terminal, role });
  const named = seats.join(", ") + (s.more ? ` +${s.more}` : "");
  return t("{nom} · {role} · sur {postes}", { nom: s.terminal, role, postes: named });
}

export function RoleMenu({
  data,
  busy,
  onChoose,
}: {
  data: Direx;
  busy: boolean;
  onChoose: (role: DirexRole, cellule: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const me = data.me;
  const [role, setRole] = useState<DirexRole>(me.role);
  const [cellule, setCellule] = useState(me.cellule ?? data.cellules[0] ?? "");
  const box = useRef<HTMLDivElement>(null);
  // Échap (pile partagée : il ne ferme que ce menu) et appui dehors.
  usePopover(open, () => setOpen(false), box);
  useEffect(() => {
    if (!open) return;
    setRole(me.role);
    setCellule(me.cellule ?? data.cellules[0] ?? "");
  }, [open]);
  const Icon = ROLE_ICON[me.role];
  const unchanged = role === me.role && (role !== "cellule" || cellule === me.cellule);
  const others = data.stations.filter((s) => !s.mine);

  return (
    <div className="relative" ref={box}>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t("Rôle de ce poste dans la DIREX")}
      >
        <Icon size={14} className={me.role === "animateur" ? undefined : "text-accent"} />
        {me.read_only ? t("Observateur (lecture seule)") : roleLabel(me.role, me.label)}
        <ChevronDown size={14} className="text-ink-muted" />
      </Button>
      <Presence show={open}>
        {open && (
          <Motion
            origin="top"
            role="dialog"
            aria-label={t("Rôle de ce poste")}
            className="absolute top-full right-0 z-30 mt-1 w-[330px] max-w-[calc(100vw-16px)] rounded-xl border border-line bg-surface shadow-float"
          >
            <div className="space-y-1 p-1">
              <SectionLabel inset className="px-2 pt-2 pb-1">{t("Rôle de ce poste")}</SectionLabel>
              {CHOICES.map((c) => {
                const ChoiceIcon = ROLE_ICON[c.role];
                const active = role === c.role;
                return (
                  <div key={c.role}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={!me.identified || me.read_only}
                      onClick={() => setRole(c.role)}
                      className={clsx(
                        "flex min-h-8 w-full items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors disabled:opacity-40",
                        active ? "bg-accent-soft" : "hover:bg-hover",
                      )}
                    >
                      <ChoiceIcon size={14} className={clsx("mt-0.5 shrink-0", active ? "text-accent" : "text-ink-soft")} />
                      <span className="min-w-0 flex-1">
                        <span className={clsx("block text-ui font-semibold", active ? "text-accent" : "text-ink")}>{t(c.label)}</span>
                        <span className="block text-meta leading-snug text-ink-muted">{t(c.hint)}</span>
                      </span>
                      {active && <Check size={14} className="mt-0.5 shrink-0 text-accent" />}
                    </button>
                    {c.role === "cellule" && active && (
                      <Motion origin="fade" className="px-2 pt-1 pb-1.5 pl-8">
                        <Select
                          className={inputClass}
                          value={cellule}
                          onChange={(e) => setCellule(e.target.value)}
                          aria-label={t("Cellule de ce poste")}
                        >
                          {data.cellules.map((name) => (
                            <option key={name} value={name}>{term(name)}</option>
                          ))}
                        </Select>
                      </Motion>
                    )}
                  </div>
                );
              })}
            </div>
            {me.read_only ? (
              <p className="border-t border-line px-4 py-2.5 text-meta leading-snug text-ink-muted">
                {t("Dossier en lecture seule pour vous : vous suivez la DIREX en observateur, sans y écrire.")}
              </p>
            ) : me.identified ? (
              <div className="flex justify-end border-t border-line px-3 py-2.5">
                <Button
                  disabled={busy || unchanged}
                  onClick={() => {
                    onChoose(role, role === "cellule" ? cellule : null);
                    setOpen(false);
                  }}
                >
                  {t("Appliquer")}
                </Button>
              </div>
            ) : (
              <p className="border-t border-line px-4 py-2.5 text-meta leading-snug text-ink-muted">
                {t("Poste non identifié par la licence : il reste animateur.")}
              </p>
            )}
            {others.length > 0 && (
              <div className="border-t border-line px-4 py-2.5">
                <SectionLabel inset className="mb-1.5">
                  {others.some((s) => s.seats !== undefined) ? t("Autres participants") : t("Autres postes du dossier")}
                </SectionLabel>
                <ul className="space-y-1">
                  {others.map((s) => {
                    const StationIcon = ROLE_ICON[s.role];
                    return (
                      <li key={`${s.terminal}-${s.label}`} className="flex min-h-7 items-center gap-2 text-ui text-ink">
                        <StationIcon size={14} className="shrink-0 text-ink-muted" />
                        <span className="min-w-0 flex-1 truncate">
                          {s.seats !== undefined ? participantLine(s) : terminalName(s.terminal)}
                        </span>
                        {s.seats === undefined && <span className="shrink-0 text-ink-muted">{roleLabel(s.role, s.label)}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Motion>
        )}
      </Presence>
    </div>
  );
}
